(() => {
  const SearchPanel = customElements.get('peak-search-panel');
  if (!SearchPanel || customElements.get('peak-cart-panel')) return;

  // Extend Search's portal lifecycle, animation, Escape and scroll ownership.
  // Cart keeps its own section rendering so Dawn's page/mobile selectors stay intact.
  class PeakCartPanel extends SearchPanel {
    connectedCallback() {
      if (!this.layer || !this.trigger) return;
      super.connectedCallback();
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.dialog = this.layer.querySelector('[role="dialog"]');
      this.closeButton = this.layer.querySelector('.peak-search__close');
      this.input = null;
      this.placeholderTrigger = this.trigger;
      this.background = [];
      this.onHeaderClick = (event) => {
        const link = event.target.closest('.header__icon--cart, [data-peak-cart-open]');
        if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.trigger = link;
        if (link.matches('.header__icon--cart') && !this.desktop.matches && this.opened) this.close();
        else window.openPeakCart();
      };
      this.onBreakpoint = () => {
        if (this.openedOnDesktop !== this.desktop.matches) this.close({ immediate: true, restoreFocus: false });
        this.updateTriggerAccessibility();
      };
      this.onPageHide = () => this.close({ immediate: true, restoreFocus: false });
      document.addEventListener('click', this.onHeaderClick, true);
      this.desktop.addEventListener('change', this.onBreakpoint);
      window.addEventListener('pagehide', this.onPageHide);
      this.updateTriggerAccessibility();
      this.layer.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-peak-cart-clear]');
        if (clear) {
          event.preventDefault();
          if (!clear.disabled && !clear.hidden) this.changeCart({}, false, true);
          return;
        }
        const remove = event.target.closest('[data-peak-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.changeCart({ id: remove.dataset.peakCartRemove, quantity: 0 });
      });
      this.layer.addEventListener('change', (event) => {
        if (event.target.matches('.quantity__input')) {
          // Dawn's minus control finishes applying its minimum-to-zero step
          // immediately after dispatching change.
          setTimeout(() => this.changeQuantity(event.target), 0);
        } else if (event.target.id === 'PeakCart-Note') {
          this.changeCart({ note: event.target.value }, true);
        }
      });
      this.layer.addEventListener('submit', (event) => {
        if (this.busy) event.preventDefault();
      });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // ProductForm calls the notification renderer after publishing; its
        // bundled Peak sections are rendered there without a duplicate fetch.
        if (event.source === 'product-form' && event.cartData?.sections && 'peak-desktop-cart' in event.cartData.sections) return;
        if (this.opened && !this.busy && event.source !== 'peak-desktop-cart') this.refresh();
      });
    }

    disconnectedCallback() {
      if (!this.layer || !this.trigger) return;
      this.close({ immediate: true, restoreFocus: false });
      document.removeEventListener('click', this.onHeaderClick, true);
      this.desktop?.removeEventListener('change', this.onBreakpoint);
      window.removeEventListener('pagehide', this.onPageHide);
      this.unsubscribe?.();
      this.trigger = this.placeholderTrigger;
      super.disconnectedCallback();
    }

    resetSearchState() {}
    pinMobileHeader() {
      if (!this.desktop.matches) super.pinMobileHeader();
    }
    unpinMobileHeader(options) {
      if (this.mobileHeaderRestore) super.unpinMobileHeader(options);
    }
    emitState(open) {
      document.dispatchEvent(new CustomEvent('peak:cart-state', { detail: { open } }));
    }

    updateTriggerAccessibility() {
      const link = document.getElementById('cart-icon-bubble');
      if (!link) return;
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'PeakCart-Dialog');
      link.setAttribute('aria-expanded', String(this.opened));
      if (!link.querySelector('.peak-cart__trigger-close')) {
        const icon = this.closeButton.querySelector('svg').cloneNode(true);
        icon.classList.add('peak-cart__trigger-close');
        link.append(icon);
      }
    }

    open({ refresh = true } = {}) {
      if (this.opened) return;
      if (!this.desktop.matches) document.dispatchEvent(new CustomEvent('peak:cart-opening'));
      document.querySelectorAll('peak-search-panel').forEach((search) => {
        if (search.opened || search.closeTimer) search.close({ immediate: true, restoreFocus: false });
      });
      const dawnDrawer = document.querySelector('cart-drawer.active');
      if (dawnDrawer) dawnDrawer.close();
      this.input = this.closeButton;
      this.openedOnDesktop = this.desktop.matches;
      super.open();
      this.dialog.focus({ preventScroll: true });
      this.background = (this.desktop.matches ? [...document.body.children] : [...document.querySelectorAll('#MainContent, .shopify-section-group-footer-group')])
        .filter((element) => element !== this.layer && !element.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName));
      this.background.forEach((element) => { element.inert = true; });
      if (refresh) this.refresh();
    }

    renderContents(parsedState) {
      this.trigger = document.getElementById('cart-icon-bubble') || this.placeholderTrigger;
      this.layer.querySelector('[data-peak-cart-status]').textContent = '';
      this.open({ refresh: false });
      try {
        this.renderSections(parsedState.sections || {});
        const addedItem = [...this.layer.querySelectorAll('[data-cart-key]')]
          .find((item) => item.dataset.cartKey === parsedState.key);
        addedItem?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } catch (error) {
        // A missing section (including a mobile-to-desktop resize) needs only
        // the existing read/refresh path, never another add request.
        this.refresh();
      }
    }

    close(options = {}) {
      if (!this.opened) {
        super.close(options);
        return;
      }
      this.background.forEach((element) => { element.inert = false; });
      this.background = [];
      super.close(options);
      // Search's deferred focus callback must not focus a closing cart surface.
      this.input = null;
    }

    onKeydown(event) {
      super.onKeydown(event);
      if (event.key !== 'Tab' || !this.opened) return;
      const focusable = (this.desktop.matches ? getFocusableElements(this.dialog) : [...getFocusableElements(document.querySelector('.section-header')), ...getFocusableElements(this.dialog)]).filter((element) => element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    // ProductForm can use Peak directly even when Dawn's cart mode is "page".
    getSectionsToRender() {
      return this.sections().map((id) => ({ id }));
    }

    setActiveElement() {
      this.trigger = document.getElementById('cart-icon-bubble') || this.placeholderTrigger;
    }

    sections() {
      const sections = ['peak-desktop-cart', 'cart-icon-bubble'];
      const pageFooter = document.getElementById('main-cart-footer');
      if (pageFooter) sections.push(pageFooter.dataset.id);
      return sections;
    }

    setBusy(busy) {
      this.busy = busy;
      const content = this.layer.querySelector('[data-peak-cart-content]');
      content.setAttribute('aria-busy', String(busy));
      const clear = this.layer.querySelector('[data-peak-cart-clear]');
      clear.disabled = busy || content.dataset.count === '0';
      content.querySelectorAll('button, input, textarea').forEach((control) => { control.disabled = busy; });
      // Wallet buttons use shadow DOM; inert also prevents submitting stale totals.
      const wallets = content.querySelector('.peak-cart__accelerated');
      if (wallets) wallets.inert = busy;
      if (!busy && this.refreshPending) {
        this.refreshPending = false;
        queueMicrotask(() => this.refresh());
      }
    }

    showError(message) {
      const status = this.layer.querySelector('[data-peak-cart-status]');
      status.textContent = `${message} `;
      const fallback = document.createElement('a');
      fallback.href = routes.cart_url;
      fallback.textContent = window.accessibilityStrings?.viewCart || 'View cart';
      status.append(fallback);
    }

    async fetchSections() {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', this.sections().join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(cartStrings.error);
      return response.json();
    }

    renderSections(sections, focusKey) {
      const html = new DOMParser().parseFromString(sections['peak-desktop-cart'] || '', 'text/html');
      let content = html.querySelector('[data-peak-cart-content]');
      if (!content) throw new Error(cartStrings.error);
      const current = this.layer.querySelector('[data-peak-cart-content]');
      const scrollTop = current.querySelector('.peak-cart__items')?.scrollTop || 0;
      const hadFocus = current.contains(document.activeElement);
      current.dataset.count = content.dataset.count;
      const headingCount = this.layer.querySelector('[data-peak-cart-count]');
      const updatedCount = html.querySelector('[data-peak-cart-count]');
      if (headingCount && updatedCount) headingCount.textContent = updatedCount.textContent;
      const clear = this.layer.querySelector('[data-peak-cart-clear]');
      const clearHadFocus = document.activeElement === clear;
      clear.hidden = content.dataset.count === '0';
      clear.disabled = this.busy || clear.hidden;
      // Dawn's helper executes Shopify's returned checkout initialization scripts.
      HTMLUpdateUtility.setInnerHTML(current, content.innerHTML);
      content = current;
      const list = content.querySelector('.peak-cart__items');
      if (list) list.scrollTop = scrollTop;
      const icon = document.getElementById('cart-icon-bubble');
      if (icon && sections['cart-icon-bubble']) {
        icon.innerHTML = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section').innerHTML;
      }
      const pageFooter = document.getElementById('main-cart-footer');
      if (pageFooter && sections[pageFooter.dataset.id]) {
        // Dawn refreshes the page items' innerHTML on cartUpdate; keep the
        // outer empty-state class in sync when this drawer changes that cart.
        document.querySelector('cart-items')?.classList.toggle('is-empty', content.dataset.count === '0');
        const footer = new DOMParser().parseFromString(sections[pageFooter.dataset.id], 'text/html').querySelector('#main-cart-footer');
        pageFooter.classList.toggle('is-empty', content.dataset.count === '0');
        const totals = pageFooter.querySelector('.js-contents');
        const updatedTotals = footer?.querySelector('.js-contents');
        if (totals && updatedTotals) totals.innerHTML = updatedTotals.innerHTML;
        const checkout = pageFooter.querySelector('[name="checkout"]');
        if (checkout) checkout.disabled = content.dataset.count === '0';
      }
      this.updateTriggerAccessibility();
      window.Shopify?.PaymentButton?.init?.();
      if (this.opened && (hadFocus || focusKey || (clearHadFocus && clear.hidden))) {
        const input = [...content.querySelectorAll('[data-key]')].find((element) => element.dataset.key === focusKey);
        (input || content.querySelector('a') || this.closeButton).focus({ preventScroll: true });
      }
    }

    async refresh() {
      if (this.busy) {
        this.refreshPending = true;
        return;
      }
      this.setBusy(true);
      this.layer.querySelector('[data-peak-cart-status]').textContent = '';
      try {
        this.renderSections(await this.fetchSections());
      } catch (error) {
        this.showError(cartStrings.error);
      } finally {
        this.setBusy(false);
      }
    }

    changeQuantity(input) {
      if (!input.isConnected || this.busy) return;
      const quantity = input.value.trim() === '' ? NaN : Number(input.value);
      const min = Number(input.dataset.min);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step) || 1;
      let message = '';
      if (!Number.isInteger(quantity) || quantity < 0) message = cartStrings.error;
      else if (quantity !== 0 && quantity < min) message = quickOrderListStrings.min_error.replace('[min]', min);
      else if (quantity > max) message = quickOrderListStrings.max_error.replace('[max]', max);
      else if (quantity % step !== 0) message = quickOrderListStrings.step_error.replace('[step]', step);
      input.setCustomValidity(message);
      if (message) {
        input.reportValidity();
        input.value = input.defaultValue;
        input.select();
        return;
      }
      this.changeCart({ id: input.dataset.key, quantity });
    }

    async changeCart(change, note = false, clear = false) {
      if (this.busy) return;
      this.setBusy(true);
      const status = this.layer.querySelector('[data-peak-cart-status]');
      status.textContent = '';
      try {
        const response = await fetch(clear ? `${routes.cart_url}/clear.js` : note ? routes.cart_update_url : routes.cart_change_url, {
          ...fetchConfig(), credentials: 'same-origin',
          body: JSON.stringify({ ...change, sections: this.sections(), sections_url: window.location.pathname }),
        });
        const state = await response.json();
        if (!response.ok || state.errors || state.status) {
          throw new Error(state.description || state.message || state.errors || cartStrings.error);
        }
        // A section-render failure must never repeat the successful mutation.
        const sections = state.sections?.['peak-desktop-cart'] ? state.sections : await this.fetchSections();
        this.renderSections(sections, change.id);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'peak-desktop-cart', cartData: state });
        const updated = state.items.find((item) => item.key === change.id);
        if (!note && change.quantity > 0 && updated && updated.quantity !== change.quantity) {
          status.textContent = cartStrings.quantityError.replace('[quantity]', updated.quantity);
        }
      } catch (error) {
        this.layer.querySelectorAll('.quantity__input').forEach((input) => { input.value = input.defaultValue; });
        this.showError(error.message || cartStrings.error);
      } finally {
        this.setBusy(false);
      }
    }
  }
  // Returns false only when the theme's normal fallback should handle the action.
  // The existing controller/CSS already select desktop drawer or mobile sheet.
  window.openPeakCart = (state = {}) => {
    const cart = document.querySelector('peak-cart-panel');
    if (typeof cart?.renderContents !== 'function' || !cart.dialog) return false;
    const notification = document.querySelector('cart-notification');
    if (notification?.notification?.classList.contains('active')) notification.close();
    if (cart.busy) {
      cart.open({ refresh: false });
      cart.refresh();
    } else {
      cart.renderContents(state);
    }
    return true;
  };
  customElements.define('peak-cart-panel', PeakCartPanel);
})();
